import { z } from 'zod';
// Typed capability catalogue — the enforced form of the cross-repo authorization
// contract ems-domain/PERMISSIONS.md (ADR-025). Single source of truth for:
// backend role-write validation, the can() decision point's includes/requires
// resolution, and (consumed later) the frontend role editor + permission-gated
// render. Consumers must NOT hand-author key lists — derive everything from here.
//
// Encoded breadth: LIVE + Locked keys only — their names are settled, and keys
// never rename once seeded, so this list is append-only-safe. The draft rest of
// the monitoring and reports families, and the later ticket kinds, join at
// their own design sessions per the catalogue's status tiers.
//
// Two relationships (ADR-025), each declared ONCE, here:
//   includes — holding X grants Y downward (only the ordered action set);
//              resolved live at check-time (expandIncludes), never stored.
//   requires — X is meaningful only if Y is also held; self-bounding, so it
//              gates the editor + write-hygiene, NOT the can() decision.
// Optional-module flags (columns on `projects`). A key tagged with one is only
// offered when that module is on. The runtime gate is wired at TWO points
// (ADR-044): decision-time — `getPermissions` drops off-module keys from the
// effective set and `can()` denies an off-module capability for everyone (admins
// included, BEFORE the short-circuit); and write-time — role-write drops
// off-module grants (backstop). The catalogue is the single source for which key
// maps to which flag (`entitlementOf` / `filterByEntitlements`).
export const ENTITLEMENT_FLAGS = ['has_invoicing', 'has_field_force', 'has_vehicles'];
export const EntitlementFlagSchema = z.enum(ENTITLEMENT_FLAGS);
export const CAPABILITY_CATALOGUE = [
    // ── Users & access — LIVE ───────────────────────────────────────────────
    // The ONE people key (ADR-050): accounts + roles for the people in the holder's
    // managed-roles list (whom), the SCOPE of those people bounded by the holder's own
    // scope (what), and the accountability chains at the places the holder covers —
    // narrowed by the level-set on roles.responsibility_levels; the assign action is
    // has_field_force-gated at the route, never on the key.
    { key: 'users:manage' },
    { key: 'api_docs:read' },
    // Other users' restricted documents (identity · contracts) — HR-grade PII; a
    // STANDALONE key, deliberately separate from users:manage; a user always reads
    // their own (ADR-068 §5).
    { key: 'user.documents:read' },
    // ── Audit — LIVE. The project audit-trail read surface. Admin-implicit
    // (project_admin sees it without the key); delegable to a trusted auditor. The
    // per-entity History subset rides the host entity's own read gate, not this key.
    { key: 'audit:read' },
    // ── Sites — Locked ──────────────────────────────────────────────────────
    { key: 'site:read' },
    { key: 'site:write', includes: ['site:read'] },
    { key: 'site:lifecycle', requires: ['site:write'] },
    { key: 'site:export', requires: ['site:read'] },
    { key: 'site:import', requires: ['site:write'] },
    { key: 'site.solution_config:write', requires: ['site:read'] },
    { key: 'site.contractual_config:write', requires: ['site:read'], entitlement: 'has_invoicing' },
    // The site folder's restricted documents (contracts today; `access` joins on
    // need) — a facet key on top of site:read, hidden entirely without it (ADR-068 §5).
    { key: 'site.legal:read', requires: ['site:read'] },
    // ── Files — Locked (ems-domain PERMISSIONS §Files). Read is inherited from
    // the anchor (no `files:read`; the facets `site.legal:read` and
    // `user.documents:read` stay under their entities); the three verbs apply on
    // every anchor kind in the holder's scope. Writing a file is the family's,
    // never the record's — `site:write` / `asset:write` edit the RECORD and grant
    // nothing on its files (ADR-068 §5).
    { key: 'files:upload' },
    { key: 'files:manage', includes: ['files:upload'] },
    { key: 'files:review' },
    // ── Contractual configuration — Locked ──────────────────────────────────
    { key: 'contractual_config:manage', entitlement: 'has_invoicing' },
    // ── Invoicing cycle verbs — Locked (ems-domain PERMISSIONS §Invoicing).
    // Encoded ahead of the cycle routes so roles can be prepared from the
    // editor; a granted key is inert until its surfaces land.
    { key: 'invoicing:read', entitlement: 'has_invoicing' },
    { key: 'invoicing:validate', includes: ['invoicing:read'], entitlement: 'has_invoicing' },
    { key: 'invoicing:validate_exception', requires: ['invoicing:validate'], entitlement: 'has_invoicing' },
    // ── Assets — Locked ─────────────────────────────────────────────────────
    { key: 'asset:read' },
    { key: 'asset:write', includes: ['asset:read'] },
    { key: 'asset:export', requires: ['asset:read'] },
    { key: 'asset:import', requires: ['asset:write'] },
    { key: 'asset.private_fields:read', requires: ['asset:read'] },
    { key: 'asset.books:read', requires: ['asset:read'] },
    // Finance ⊥ ops: books:write needs only asset:read (not asset:write).
    { key: 'asset.books:write', requires: ['asset:read'], includes: ['asset.books:read'] },
    // Local products-catalog rows (instance-global; born from a search's
    // not-found end — ADR-065; sibling of consumable.types:manage).
    { key: 'asset.models:manage' },
    { key: 'asset.history:read_cross_project', platformTier: true },
    // ── Warehouses & consumables — Locked (ems-domain PERMISSIONS §Warehouses
    // & consumables). Enforced from the stock dataset (import/export); depot
    // CRUD + the Storage/Balances surfaces land with the warehouses & fleet
    // module and the consumables verbs — a granted key is inert until its surface (the invoicing precedent). A right on the PLACE
    // opens the place's own business (its page, its record, its balances); what
    // stands in it rides the thing's own right (`asset:read` × the depot scope).
    { key: 'warehouse:read' },
    { key: 'warehouse:write', includes: ['warehouse:read'] },
    { key: 'warehouse.stock:write', requires: ['warehouse:read'] },
    { key: 'warehouse.stock:approve', requires: ['warehouse.stock:write'] },
    { key: 'consumable.types:manage' },
    // ── Vehicles & pools — Locked (ems-domain PERMISSIONS §Vehicles & pools;
    // the whole family rides `has_vehicles`). The fleet record view and its
    // verbs; which pools a holder sees is the pool scope, and a driver sees their
    // own vehicle with no key (the relationship is the grant). The fleet
    // surfaces land with the warehouses & fleet module — a granted key is inert until its surface (the
    // invoicing precedent). `vehicle:track` (the live map) stays parked to the
    // GIS wave.
    { key: 'vehicle:read', entitlement: 'has_vehicles' },
    { key: 'vehicle:write', includes: ['vehicle:read'], entitlement: 'has_vehicles' },
    { key: 'vehicle.driver:write', requires: ['vehicle:read'], entitlement: 'has_vehicles' },
    { key: 'vehicle.pool:write', requires: ['vehicle:read'], entitlement: 'has_vehicles' },
    // ── Reference data — Locked ─────────────────────────────────────────────
    { key: 'reference_data:manage' },
    // ── Tickets — the capture family, LOCKED (ems-domain PERMISSIONS §Tickets;
    // the hub design sitting 2026-09-08). Three tiers: SEE (`read` — every
    // capture ticket across the holder's scope: the hub, the site's Tickets tab,
    // the list; a PARTICIPANT sees their own without it) · DO THE WORK (no key —
    // participation grants the doer's acts) · MANAGE (the sign-off acts: close,
    // one or many · reopen · later promote). NO create key: the field-force
    // module on + logged in + the site in scope → anyone captures and the ticket
    // is minted for them. Reviewing what a photo shows stays `files:review` — a
    // files act, never a ticket act. The whole family rides `has_field_force`;
    // request + work-order keys arrive with their phases.
    { key: 'ticket.capture:read', entitlement: 'has_field_force' },
    { key: 'ticket.capture:manage', includes: ['ticket.capture:read'], entitlement: 'has_field_force' },
    // ── Teams — LIVE (ems-domain PERMISSIONS §Teams · ADR-083 §5; the family
    // rides `has_field_force`). A key for the THING, team scope for WHICH teams:
    // `read` opens the Teams page over the holder's team scope (a team's members,
    // its responsibilities, where it works); `manage` (includes read) creates and
    // disables, edits the work area, moves members in and out and sets whether
    // each can be assigned. NO key for one's own teams — a member sees their
    // teams by membership alone; team-level chains are the people key's
    // accountability action at the `team` level, resting on membership.
    { key: 'team:read', entitlement: 'has_field_force' },
    { key: 'team:manage', includes: ['team:read'], entitlement: 'has_field_force' },
    // ── Monitoring · Reports — the AREA keys LIVE (ems-domain PERMISSIONS
    // §Monitoring · Reports): `monitoring:read` opens the Monitoring area; a report
    // key opens Reports (one standalone toggle per report — seeing a report is
    // exporting it), `report:alarm_history` the first. The rest of each family
    // stays draft until its own design session.
    { key: 'monitoring:read' },
    { key: 'report:alarm_history' },
    // ── Machine contracts — machine-tier, LIVE (ADR-004 · ems-domain
    // PERMISSIONS §Machine contracts). Surfaces only ever exercised by an
    // application credential; `ingest` is the machine-producer verb (idempotent
    // latest-sighting report — the server keeps control; distinct from `write`).
    { key: 'external_identity:resolve', machineTier: true },
    // The bot fleet — how the ONE channel app learns the bots it serves (ADR-069;
    // DATA-CONTRACTS §Telegram): every project's connected bot with its token,
    // the one door a bot credential leaves ems-app through. Fleet keys only.
    { key: 'bot_fleet:read', machineTier: true },
    { key: 'solution_config_observation:ingest', machineTier: true },
    // CMDB projections — the register's machine mirror (DATA-CONTRACTS §CMDB
    // projections): full-snapshot reads over HTTP + ETag, fed by the
    // transactional outbox. Project reads bounded by the key's reach; the two
    // platform reads (projects · models) are fleet-keys-only.
    { key: 'cmdb_projection:read', machineTier: true },
    // Device targets — the collector work-list (DATA-CONTRACTS §device targets):
    // endpoints + plaintext credentials + the fronted composition, narrowed per
    // credential by api_clients.device_target_kinds (empty = identity-only).
    { key: 'device_targets:read', machineTier: true },
];
// Widened view — `as const` narrows each element to its own literal shape, so
// reading optional props (platformTier/includes/requires) needs the interface type.
const CATALOGUE = CAPABILITY_CATALOGUE;
const byKey = new Map(CATALOGUE.map((d) => [d.key, d]));
// Every catalogued key (incl. platform-tier).
export const ALL_PERMISSION_KEYS = CATALOGUE.map((d) => d.key);
// Keys a PROJECT role may grant — excludes platform-tier (sysadmin-only, cross-
// tenancy) AND machine-tier (application-credential-only, ADR-004). The role-write
// schema validates against this set, so attempting to grant either in a project
// role fails as an unknown key. This is also the on-behalf-grantable set for an
// api_client (on-behalf intersects with a human role, so nothing beyond it can
// ever take effect).
export const PROJECT_GRANTABLE_KEYS = CATALOGUE.filter((d) => !d.platformTier && !d.machineTier).map((d) => d.key);
// Machine-contract keys (ADR-004) — the machine-tier subset.
export const MACHINE_CONTRACT_KEYS = CATALOGUE.filter((d) => d.machineTier).map((d) => d.key);
// Keys grantable in an api_client's SELF (`permissions`) list — everything a
// machine may do as itself: the machine contracts plus the human capability keys
// (a reporting client holds site:read M2M). Platform-tier stays sysadmin-only.
export const API_CLIENT_SELF_GRANTABLE_KEYS = CATALOGUE.filter((d) => !d.platformTier).map((d) => d.key);
// Read-tier predicate (ADR-004 — the PAT least-privilege bound). A key is read-tier
// iff its action segment is exactly `read` or `export` AND it is neither platform-
// tier (cross-tenancy, sysadmin-only) nor machine-tier (application-credential-only).
// `export` self-bounds to what the holder can `read` (a `requires` on its `:read`
// sibling), so it belongs to the read tier. `read_cross_project` is deliberately NOT
// matched (not exactly `read`, and platform-tier regardless). This is the durable
// answer to "which capabilities are safe on a read-only credential", reusable by a
// future read-only-auditor role — not a one-off PAT list.
export function isReadTier(key) {
    const def = byKey.get(key);
    if (!def || def.platformTier || def.machineTier)
        return false;
    const action = key.slice(key.lastIndexOf(':') + 1);
    return action === 'read' || action === 'export';
}
// Keys a PERSONAL ACCESS TOKEN (user_api_keys) may carry (ADR-004 — PATs are
// read-only at foundation). The read tier, derived once. A PAT's effective set is
// still re-intersected live with the owner's own permissions per request; this only
// bounds what may be OFFERED at mint. The PAT create schema validates against it, so
// a write/manage/platform/machine key fails as an unknown key (→ 400).
export const PAT_GRANTABLE_KEYS = CATALOGUE.filter((d) => isReadTier(d.key)).map((d) => d.key);
// Transitive `requires` closure of a key set — the prerequisites a grant needs to
// be non-inert (e.g. `site:export` requires `site:read`). Used by the PAT picker to
// auto-satisfy a prerequisite on selection, so a user's `:export` pick is never
// silently dropped by `normalizeGrants` on write. Additive to the input set; order
// not significant (the caller re-orders to catalogue order).
export function expandRequires(keys) {
    const out = new Set();
    const stack = [...keys];
    while (stack.length > 0) {
        const k = stack.pop();
        if (out.has(k))
            continue;
        out.add(k);
        const req = byKey.get(k)?.requires;
        if (req)
            for (const x of req)
                stack.push(x);
    }
    return out;
}
// Zod enum for PAT-create validation. An unknown / non-read-tier key fails here (→ 400).
export const PatPermissionKeySchema = z.enum(PAT_GRANTABLE_KEYS);
// Zod enum for role-write validation. An unknown OR platform-tier key fails here
// (→ 400), which is the "key exists / action allowed" guard.
export const ProjectPermissionKeySchema = z.enum(PROJECT_GRANTABLE_KEYS);
export function getCapability(key) {
    return byKey.get(key);
}
// Transitive closure of a key set under `includes` — the effective capability set
// consumed by hasPermission/can() and built once into the permission cache entry.
export function expandIncludes(keys) {
    const out = new Set();
    const stack = [...keys];
    while (stack.length > 0) {
        const k = stack.pop();
        if (out.has(k))
            continue;
        out.add(k);
        const inc = byKey.get(k)?.includes;
        if (inc)
            for (const x of inc)
                stack.push(x);
    }
    return out;
}
// The optional-module flag gating a key, if any (undefined ⇒ core / always-on).
// Single source for the key→entitlement relation consumed by the decision-time
// gate (can()), the cache pre-filter, the role-write backstop, and the frontend
// render gate (ADR-044).
export function entitlementOf(key) {
    return byKey.get(key)?.entitlement;
}
// Drop keys whose optional module is OFF. `on` = the project's ON-set. A key with
// no entitlement (core / always-on) is always kept; an unknown key is kept here
// (entitlement filtering is orthogonal to existence — expandIncludes / normalizeGrants
// own that). Apply AFTER expandIncludes so `write ⊇ read` is resolved first
// (ADR-044). Input order preserved (Set insertion order); dups collapsed.
export function filterByEntitlements(keys, on) {
    const out = new Set();
    for (const k of keys) {
        const e = byKey.get(k)?.entitlement;
        if (e && !on.has(e))
            continue;
        out.add(k);
    }
    return out;
}
// Diagnostic: granted keys whose `requires` are unsatisfied by the set's includes-
// expansion (i.e. inert grants). Drives editor feedback; not the can() decision.
export function unmetRequires(keys) {
    const expanded = expandIncludes(keys);
    const out = [];
    for (const k of new Set(keys)) {
        const req = byKey.get(k)?.requires;
        if (!req)
            continue;
        const missing = req.filter((r) => !expanded.has(r));
        if (missing.length > 0)
            out.push({ key: k, missing });
    }
    return out;
}
// Normalize a granted key set on write by DROPPING inert keys (requires
// unsatisfied), to a fixpoint — a dropped prerequisite can render a dependent
// inert in turn. We never ADD a prerequisite: that would widen privilege beyond
// what the admin chose. Unknown keys are dropped here too (the Zod enum rejects
// them earlier; this is defence-in-depth). Input order is preserved.
export function normalizeGrants(keys) {
    let kept = [...new Set(keys)].filter((k) => byKey.has(k));
    for (;;) {
        const expanded = expandIncludes(kept);
        const next = kept.filter((k) => (byKey.get(k)?.requires ?? []).every((r) => expanded.has(r)));
        if (next.length === kept.length)
            return next;
        kept = next;
    }
}
